import { EditProjectFormValues } from 'app/features/project/shared/types/form';
import { UseProjectMutationsReturn } from 'app/features/project/shared/types/mutations';
import { ESourceControl } from '@xmcloud/types';
import { UseMutationResult } from 'react-query';
import { AxiosResponse } from 'axios';
import { IRepositoryHookSubscritionPayload } from 'app/models/repositoriesModel';

interface UpdateHandlerParams {
	projectId: string;
	mutations: UseProjectMutationsReturn;
	subscribeHook?: UseMutationResult<
		AxiosResponse<string, any>,
		unknown,
		IRepositoryHookSubscritionPayload,
		unknown
	>;
}

/**
 * Creates handlers for submitting project updates.
 *
 * @param {UpdateHandlerParams} params - The parameters needed for update operations
 * @param {string} params.projectId - The ID of the project being updated
 * @param {UseProjectMutationsReturn} params.mutations - Mutation functions for project updates
 * @param {UseMutationResult} params.subscribeHook - Optional mutation for subscribing to repository hooks (required for ADO)
 * @returns {Object} An object containing three handler functions:
 *   - handleBothChanges: Updates both project name and repository details
 *   - handleNameChange: Updates only the project name
 *   - handleRepoChange: Updates only the repository details
 */
export const createSubmitHandlers = ({
	projectId,
	mutations,
	subscribeHook,
}: UpdateHandlerParams) => {
	const { updateProject, linkRepository, unlinkRepository } = mutations;
	const ADO_REPO_PATH_SEGMENTS = 3; // ADO repos are expressed as org/project/repo

	/**
	 * Helper function to subscribe to ADO repository hooks if needed
	 * This is non-blocking - if subscription fails, we log it but don't fail the operation
	 * The subscription might already exist or there might be a transient issue
	 */
	const subscribeToHookIfNeeded = async (
		values: EditProjectFormValues,
	): Promise<void> => {
		if (!subscribeHook) {
			return;
		}

		// Check if this is an ADO provider
		// We check the form's provider value - it should be set correctly when repository changes
		const isADOProvider = values.provider === ESourceControl.ADO;

		// Additional check: ADO repositories have a specific format (org/project/repo)
		// This is a fallback check in case the provider value isn't set correctly
		const isADORepositoryFormat =
			values.repository?.split('/').length === ADO_REPO_PATH_SEGMENTS;

		let shouldSubscribe = isADOProvider;

		if (!isADOProvider && isADORepositoryFormat && !values.provider) {
			// Avoid relying on brittle path-based detection; surface that provider is missing
			console.warn(
				'[EditProject] Provider missing for repository; skipping hook subscription. Repo:',
				values.repository,
			);
			shouldSubscribe = false;
		}

		if (!shouldSubscribe) {
			return;
		}

		const subscriptionPayload: IRepositoryHookSubscritionPayload = {
			repositoryName: values.repository,
			integrationId: values.sourceControlIntegrationId,
		};

		// Ensure we have required values
		if (
			!subscriptionPayload.repositoryName ||
			!subscriptionPayload.integrationId
		) {
			return;
		}

		try {
			await subscribeHook.mutateAsync(subscriptionPayload);
		} catch (error: any) {
			// Log the error but don't fail the entire operation
			// The subscription might already exist (409) or there might be a transient issue
			// We still want the repository linking to succeed
			const status = error?.response?.status;
			const isConflict = status === 409; // Conflict - subscription might already exist
			const errorMessage =
				error?.response?.data?.message ||
				error?.message ||
				'Unknown error';

			if (!isConflict) {
				console.error('Failed to subscribe to repository hooks:', {
					error: errorMessage,
					status,
					payload: subscriptionPayload,
					fullError: error,
				});
				// Still don't re-throw - allow the operation to continue
				// The repository is already linked, subscription failure shouldn't block that
			}
		}
	};

	const handleBothChanges = async (values: EditProjectFormValues) => {
		try {
			await unlinkRepository.mutateAsync(projectId);
			await linkRepository.mutateAsync({
				repository: values.repository,
				repositoryId: values.repositoryId.toString(),
				integrationId: values.sourceControlIntegrationId,
			});
			// Subscribe to hooks for ADO repositories after linking
			await subscribeToHookIfNeeded(values);
			await updateProject.mutateAsync({
				name: values.name,
			});
		} catch (error) {
			throw error;
		}
	};

	const handleNameChange = async (values: EditProjectFormValues) => {
		try {
			await updateProject.mutateAsync({
				name: values.name,
			});
		} catch (error) {
			throw error;
		}
	};

	const handleRepoChange = async (values: EditProjectFormValues) => {
		try {
			await unlinkRepository.mutateAsync(projectId);
			await linkRepository.mutateAsync({
				repository: values.repository,
				repositoryId: values.repositoryId.toString(),
				integrationId: values.sourceControlIntegrationId,
			});
			// Subscribe to hooks for ADO repositories after linking
			await subscribeToHookIfNeeded(values);
		} catch (error) {
			throw error;
		}
	};

	return {
		handleBothChanges,
		handleNameChange,
		handleRepoChange,
	};
};
